import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronLeft, AlertTriangle, NotebookPen, MapPin, Save, Eye, EyeOff, Edit3, FileText, FileDown, Sparkles, Share2, CalendarClock, ShieldCheck, Lock, AlertCircle, UserPlus, Trash2 } from "lucide-react";
import { ServiceUserDrawer } from "./ServiceUsers";
import VisitTrail from "@/components/VisitTrail";
import { StructuredListEditor, MEDICATION_FIELDS, HEALTH_CONDITION_FIELDS, ALLERGY_FIELDS, EducationChildEditor } from "@/components/CarePlanStructuredEditors";
import ShareOPPModal from "@/components/ShareOPPModal";
import { fmtDate, fmtDateTimeShort, fmtISODate } from "@/lib/dates";
import { openAuthedPrintable } from "@/lib/printables";

const RM_LIKE = ["registered_manager", "deputy_manager", "super_admin"];
const RM_ONLY = ["registered_manager", "super_admin"];
const MGMT = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "administrator", "super_admin"];

const CP_SECTIONS = [
  { key: "My One-Page Profile",           description: "What people like and admire about me; what's important to me; how to support me. Printable grab-sheet." },
  { key: "About Me",                      description: "Personal history, what matters to the person, life story." },
  { key: "People Involved in My Care",    description: "Family, friends, professionals, advocates." },
  { key: "My Goals",                      description: "Short and long-term goals, aspirations, outcomes the person wants." },
  { key: "My Health Needs",               description: "Medical conditions, health monitoring, NHS appointments." },
  { key: "Medication",                    description: "Medications, doses, route, PRN protocols." },
  { key: "Eating, Drinking & Nutrition",  description: "Diet, swallow safety, fluids, preferences, MUST scoring." },
  { key: "Mobility & Moving and Handling",description: "Transfers, equipment, falls prevention." },
  { key: "Personal Care & Continence",    description: "Bathing, dressing, continence aids, dignity preferences." },
  { key: "Sleep",                         description: "Sleep routine, night-time support, waking risks." },
  { key: "Communication",                 description: "How the person communicates — Makaton, PECS, verbal, non-verbal, distress responses." },
  { key: "Sensory Profile",               description: "Sensory likes/dislikes, environmental triggers, regulation strategies." },
  { key: "My Support Needs",              description: "What support is needed and how the person wants it delivered." },
  { key: "My Daily Routine",              description: "Preferred routine, morning/evening, activities. Drives visit task checklists." },
  { key: "Education / Activity / Work",   description: "School / college (children) or work / community activity (adults)." },
  { key: "Behavioural Support / PBS",     description: "Triggers, de-escalation, positive behaviour support strategies." },
  { key: "Risk Summary",                  description: "Read-only — auto-pulled from active risk assessments.", readOnly: true },
  { key: "Safeguarding",                  description: "Active alerts, S42 / S47, MASH involvement. Restricted." },
  { key: "Restrictions & DoLS",           description: "Deprivation of Liberty Safeguards, restrictive interventions. Restricted." },
  { key: "Finances",                      description: "Appointee, deputyship, pocket-money handling. Restricted." },
  { key: "End of Life / ReSPECT",         description: "Advance care plan, ReSPECT form, ceiling of treatment. Or mark not applicable with reason." },
];

export default function ServiceUserDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);

  const load = () => api.get(`/service-users/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const su = data.service_user;
  const isMgmt = MGMT.includes(user.role);

  return (
    <div className="space-y-5">
      <Link to="/service-users" className="text-sm text-slate-600 hover:text-[#0D7377] inline-flex items-center gap-1">
        <ChevronLeft className="h-4 w-4" /> All service users
      </Link>

      <header className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 cc-glow">
        {/* ADD-13/14: Status banner for hospitalised / discharged / deceased */}
        {(["hospitalised", "discharged", "deceased"].includes(su.status)) && (
          <div data-testid="su-status-banner" className={`mb-3 rounded-md border px-3 py-2 text-sm ${su.status === "hospitalised" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-slate-100 border-slate-300 text-slate-800"}`}>
            <strong>{su.status === "hospitalised" ? "Currently in hospital" : su.status === "discharged" ? "Discharged from service" : "Deceased"}</strong>
            {su.status === "hospitalised" && (su.hospital_admissions || []).filter((a) => !a.discharged_at).map((a) => (
              <span key={a.id}> — {a.hospital_name} (admitted {fmtDate(a.admitted_at)})</span>
            ))}
            {(su.status === "discharged" || su.status === "deceased") && <span> · This record is locked. New visits and care-plan edits are disabled.</span>}
          </div>
        )}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">{su.full_name}</h1>
            <p className="text-sm text-slate-600">
              {su.preferred_name && <>"{su.preferred_name}" · </>}
              DOB {su.dob || "—"} · {(su.service_types || []).join(", ")}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              <span
                data-testid="su-type-badge"
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wide ${su.service_user_type === "child" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-slate-100 text-slate-700 border-slate-300"}`}
              >
                {su.service_user_type === "child" ? "Child / Young person" : "Adult"}
              </span>
              {su.service_user_type === "child" && su.placing_authority && (
                <span className="text-slate-600">Placing authority: <strong>{su.placing_authority}</strong></span>
              )}
            </div>
            {su.completion && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                {su.completion.percent_complete < 100 ? (
                  <span data-testid="su-incomplete-banner" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                    <AlertCircle className="h-3 w-3" /> Profile {su.completion.percent_complete}% — finish: {su.completion.incomplete_tabs.join(", ")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Profile complete</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={su.status} />
            {isMgmt && (
              <Link
                to={`/rota?su=${encodeURIComponent(su.id)}`}
                data-testid="su-view-rota-button"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-300 hover:bg-slate-50 rounded-md font-medium text-slate-700"
                title="Open this service user's calendar"
              >
                <CalendarClock className="h-3.5 w-3.5" /> View rota
              </Link>
            )}
            {isMgmt && (
              <button onClick={() => setEditing(true)} data-testid="edit-su-button" className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md font-medium">
                <Edit3 className="h-3.5 w-3.5" /> Edit details
              </button>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div><p className="text-xs uppercase tracking-wider text-slate-500">Address</p><p className="text-slate-800">{su.address?.line1}, {su.address?.city} {su.address?.postcode}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-slate-500">Communication</p><p className="text-slate-800">{su.communication_needs || "—"}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-slate-500">MCA / DoLS</p><p className="text-slate-800 capitalize">{(su.mca_status || "").replace(/_/g, " ")} / {(su.dols_status || "").replace(/_/g, " ")}</p></div>
        </div>
      </header>

      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto" role="tablist">
        {[
          ["overview", "Overview"],
          ["care_plan", "Care plan"],
          ["risks", "Risk assessments"],
          ["visits", "Visits"],
          ["incidents", "Incidents"],
          ["notes", "Daily notes"],
          ["staff", "Assigned staff"],
          ["family", "Family"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`su-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${tab === k ? "text-[#0D7377] border-b-2 border-[#0D7377]" : "text-slate-600 hover:text-slate-900"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab su={su} userRole={user.role} suId={id} reload={load} />}
      {tab === "care_plan" && <CarePlanTab data={data} userRole={user.role} reload={load} suId={id} />}
      {tab === "risks" && <RisksTab risks={data.risks} suId={id} suName={su.full_name} userRole={user.role} />}
      {tab === "visits" && <VisitsTab visits={data.visits} />}
      {tab === "incidents" && <IncidentsTab incidents={data.incidents} />}
      {tab === "notes" && <NotesTab notes={data.daily_notes} suId={id} userRole={user.role} reload={load} />}
      {tab === "staff" && <AssignedStaffPanel su={su} reload={load} userRole={user.role} />}
      {tab === "family" && <FamilyMembersPanel suId={id} userRole={user.role} suName={su.full_name} />}

      {editing && (
        <ServiceUserDrawer existingId={id} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />
      )}
    </div>
  );
}

function OverviewTab({ su, userRole, suId, reload }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
        <p className="text-xs uppercase tracking-wider text-slate-500">Primary diagnosis</p>
        <p className="mt-1 text-slate-800">{su.primary_diagnosis || "—"}</p>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">GP</p>
            <p className="mt-1 text-slate-800">{su.gp_details?.name || "—"}{su.gp_details?.practice ? " · " + su.gp_details.practice : ""}</p>
            <p className="text-sm text-slate-500">{su.gp_details?.phone}</p>
            <GpVerificationStatus gp={su.gp_details} />
          </div>
          {(["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "team_leader", "super_admin"].includes(userRole)) && (
            <GpVerifyButton suId={suId} reload={reload} />
          )}
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
        <p className="text-xs uppercase tracking-wider text-slate-500">Emergency contact</p>
        {(su.emergency_contacts || []).map((c, i) => (
          <p key={i} className="text-sm text-slate-800">{c.name} ({c.relationship}) · {c.phone}</p>
        ))}
        {(!su.emergency_contacts || su.emergency_contacts.length === 0) && <p className="text-sm text-slate-500">None recorded.</p>}
      </div>
      <StatutoryLinksCard su={su} />
    </div>
  );
}

function StatutoryLinksCard({ su }) {
  const isChild = su.service_user_type === "child";
  const links = su.statutory_links || {};
  const rows = isChild
    ? [
        ["Placing authority", su.placing_authority],
        ["LA Care Plan ref", links.la_care_plan_ref],
        ["LA Placement Plan ref", links.la_placement_plan_ref],
        ["IRO", links.iro_name && `${links.iro_name}${links.iro_phone ? " · " + links.iro_phone : ""}`],
        ["Social worker", links.social_worker_name && `${links.social_worker_name}${links.social_worker_phone ? " · " + links.social_worker_phone : ""}`],
        ["Personal Education Plan", links.personal_education_plan_ref],
        ["Last LAC review", fmtDate(links.looked_after_review_date)],
      ]
    : [
        ["Care Act 2014 s25 plan", links.care_act_plan_ref],
        ["Lead professional", links.lead_professional_name && `${links.lead_professional_name}${links.lead_professional_phone ? " · " + links.lead_professional_phone : ""}`],
      ];
  const populated = rows.filter(([, v]) => v);
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 lg:col-span-3" data-testid="su-statutory-card">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs uppercase tracking-wider text-slate-500">Statutory plan references — {isChild ? "CPPCR 2010 (child)" : "Care Act 2014 s25 (adult)"}</p>
        <p className="text-[11px] text-slate-400">held by the placing authority</p>
      </div>
      {populated.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">No statutory references recorded yet. Use <strong>Edit details → Statutory plan</strong> to add them.</p>
      ) : (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {populated.map(([lbl, val]) => (
            <p key={lbl} className="text-slate-800"><span className="text-slate-500">{lbl}:</span> {val}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function CarePlanTab({ data, userRole, reload, suId }) {
  const isRm = RM_ONLY.includes(userRole);
  const isRmLike = RM_LIKE.includes(userRole);
  const su = data.service_user || {};
  const active = data.care_plan;
  const draft = data.draft_care_plan;
  const editingPlan = draft || (isRmLike && !active ? null : null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/care-plans/${suId}/history`).then(({ data }) => setHistory(data.items || [])).catch(() => {});
  }, [suId, active?.id, draft?.id]);

  const startReview = async () => {
    if (!active) return;
    await api.post(`/care-plans/${active.id}/start-review`);
    reload();
    setEditorOpen(true);
  };

  const createInitial = async () => {
    // Create a fresh draft for SUs without any plan
    await api.post("/care-plans", { service_user_id: suId, sections: {} });
    reload();
    setEditorOpen(true);
  };

  const reviewDueInfo = active ? computeReviewState(active.review_due_date) : null;

  return (
    <div className="space-y-4">
      {/* Review-date banner — always visible (BUG-05) */}
      {active && reviewDueInfo && (
        <div data-testid="care-plan-review-banner"
          className={`rounded-lg border p-4 flex items-center justify-between gap-3 ${reviewDueInfo.tone === "red" ? "bg-red-50 border-red-200 text-red-900" : reviewDueInfo.tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            <div>
              <p className="font-semibold text-sm">{reviewDueInfo.headline}</p>
              <p className="text-xs">Active version published {fmtDate(active.approved_at)} by {active.approved_by_name || "—"}</p>
            </div>
          </div>
          {isRmLike && <ManualReviewDateButton planId={active.id} reload={reload} />}
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
              Care plan
              {active && !draft && <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded"><Lock className="h-3 w-3" />Locked · Active v{active.version_number}</span>}
              {draft && <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded"><Edit3 className="h-3 w-3" />Draft v{draft.version_number}</span>}
              {!active && !draft && <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 rounded">Not yet created</span>}
            </h3>
            <p className="text-xs text-slate-500">
              {active ? `Active v${active.version_number} · review due ${fmtDate(active.review_due_date)}` : "An active care plan must be published by the Registered Manager."}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setHistoryOpen((v) => !v)} data-testid="care-plan-history-toggle" className="px-3 py-1.5 text-xs border border-slate-300 rounded-md inline-flex items-center gap-1"><FileText className="h-3 w-3" /> History ({history.length})</button>
            {active && (
              <button
                onClick={() => openAuthedPrintable(`/care-plans/${active.id}/export`)}
                data-testid="care-plan-export-pdf-button"
                className="px-3 py-1.5 text-xs border border-slate-300 rounded-md inline-flex items-center gap-1 hover:bg-slate-50"
              >
                <FileDown className="h-3 w-3" /> Export PDF
              </button>
            )}
            {active && (
              <button
                onClick={() => openAuthedPrintable(`/care-plans/${active.id}/one-page-profile`)}
                data-testid="care-plan-one-page-button"
                className="px-3 py-1.5 text-xs border border-slate-300 rounded-md inline-flex items-center gap-1 hover:bg-slate-50"
                title="A4 grab-sheet for hospital admissions, relief staff, or fridge door."
              >
                <Sparkles className="h-3 w-3" /> One-Page Profile
              </button>
            )}
            {active && isRmLike && (
              <button
                onClick={() => setShareOpen(true)}
                data-testid="care-plan-share-opp-button"
                className="px-3 py-1.5 text-xs border border-emerald-300 text-emerald-700 rounded-md inline-flex items-center gap-1 hover:bg-emerald-50"
                title="Generate a time-limited link to send via WhatsApp / SMS."
              >
                <Share2 className="h-3 w-3" /> Share OPP
              </button>
            )}
            {isRmLike && active && !draft && (
              <button onClick={startReview} data-testid="care-plan-start-review-button" className="px-3 py-1.5 bg-[#0D7377] hover:bg-[#0A5C5F] text-white text-sm rounded-md font-medium inline-flex items-center gap-1.5"><Edit3 className="h-3.5 w-3.5" /> Start review / edit</button>
            )}
            {isRmLike && !active && !draft && (
              <button onClick={createInitial} data-testid="care-plan-create-button" className="px-3 py-1.5 bg-[#0D7377] hover:bg-[#0A5C5F] text-white text-sm rounded-md font-medium">+ Create care plan</button>
            )}
            {draft && (
              <button onClick={() => setEditorOpen(true)} data-testid="care-plan-edit-draft-button" className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-md font-medium inline-flex items-center gap-1.5"><Edit3 className="h-3.5 w-3.5" /> Continue draft</button>
            )}
          </div>
        </div>

        {/* Active / Draft viewer */}
        {(active || draft) && (
          <ReadOnlyCarePlanView plan={draft || active} active={active} draft={draft} isRmLike={isRmLike} reload={reload} />
        )}
      </div>

      {historyOpen && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
          <h4 className="font-heading text-sm font-semibold text-slate-900 mb-2">Version history</h4>
          {history.length === 0 && <p className="text-sm text-slate-500">No history yet.</p>}
          <ul className="divide-y divide-slate-100" data-testid="care-plan-history-list">
            {history.map((h) => (
              <li key={h.id} className="py-2 flex items-center justify-between text-sm gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900">v{h.version_number} <StatusBadge status={h.status} /></p>
                  <p className="text-xs text-slate-500">{fmtDate(h.approved_at || h.created_at)} · {h.approved_by_name || h.authored_by_name || "—"}</p>
                </div>
                {h.review_record?.summary_of_changes && (
                  <span className="text-xs italic text-slate-500 max-w-md truncate">{h.review_record.summary_of_changes}</span>
                )}
                {(h.status === "active" || h.status === "superseded") && (
                  <button
                    onClick={() => openAuthedPrintable(`/care-plans/${h.id}/export`)}
                    data-testid={`care-plan-history-export-${h.id}`}
                    className="px-2 py-1 text-[11px] border border-slate-300 rounded-md inline-flex items-center gap-1 hover:bg-slate-50 shrink-0"
                  >
                    <FileDown className="h-3 w-3" /> PDF
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {editorOpen && draft && (
        <CarePlanEditor draft={draft} onClose={() => setEditorOpen(false)} reload={() => { reload(); }} userRole={userRole} suType={su.service_user_type || "adult"} />
      )}

      {shareOpen && active && (
        <ShareOPPModal plan={active} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
}

function computeReviewState(reviewDue) {
  if (!reviewDue) return null;
  const due = new Date(reviewDue);
  const now = new Date();
  const days = Math.round((due - now) / 86400000);
  if (days < 0) {
    return { tone: "red", days, headline: `Care plan review OVERDUE by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}` };
  }
  if (days <= 28) {
    return { tone: "amber", days, headline: `Care plan review due in ${days} day${days === 1 ? "" : "s"}` };
  }
  return { tone: "green", days, headline: `Next review due in ${days} days · ${fmtISODate(due)}` };
}

function ManualReviewDateButton({ planId, reload }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const submit = async () => {
    if (!date) return;
    await api.put(`/care-plans/${planId}/review-due-date`, { review_due_date: new Date(date).toISOString() });
    setOpen(false); setDate(""); reload();
  };
  if (!open) return <button onClick={() => setOpen(true)} data-testid="set-review-date-button" className="text-xs px-2 py-1 border border-slate-300 rounded">Set earlier date</button>;
  return (
    <div className="flex gap-1 items-center">
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="px-2 py-1 text-xs border border-slate-300 rounded" />
      <button onClick={submit} className="text-xs px-2 py-1 bg-[#0D7377] text-white rounded">Save</button>
      <button onClick={() => setOpen(false)} className="text-xs px-2 py-1 border border-slate-300 rounded">Cancel</button>
    </div>
  );
}

const VIS_LEVEL_META = {
  all_staff:      { label: "All staff",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  care_team_only: { label: "Care team only",  cls: "bg-blue-50 text-blue-700 border-blue-200" },
  restricted:     { label: "Restricted",      cls: "bg-amber-50 text-amber-800 border-amber-200" },
  manager_only:   { label: "Manager only",    cls: "bg-rose-50 text-rose-700 border-rose-200" },
};
const DEFAULT_SECTION_VIS_LEVELS = {
  "Safeguarding": "restricted",
  "Restrictions & DoLS": "restricted",
  "Finances": "restricted",
  "Medication": "care_team_only",
  "My Health Needs": "care_team_only",
  "Eating, Drinking & Nutrition": "care_team_only",
  "Mobility & Moving and Handling": "care_team_only",
  "Personal Care & Continence": "care_team_only",
  "Sleep": "care_team_only",
  "Sensory Profile": "care_team_only",
  "Behavioural Support / PBS": "care_team_only",
  "Risk Summary": "care_team_only",
  "End of Life / ReSPECT": "care_team_only",
};
function visLevelFor(plan, key) {
  return (plan?.section_visibility_levels || {})[key] || DEFAULT_SECTION_VIS_LEVELS[key] || "all_staff";
}

function ReadOnlyCarePlanView({ plan, active, draft, isRmLike, reload }) {
  const visibility = plan.section_visibility || {};
  const sections = plan.sections || {};

  const toggleVis = async (key, val) => {
    if (!isRmLike) return;
    await api.put(`/care-plans/${plan.id}/section-visibility`, { [key]: val });
    reload();
  };

  return (
    <div className="space-y-4">
      {plan.locked && plan.signoff && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 flex items-start gap-2" data-testid="care-plan-locked-banner">
          <Lock className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-900">
            <p className="font-semibold">Version {plan.signoff.version} signed and locked.</p>
            <p className="text-xs">Signed by <strong>{plan.signoff.signed_by_name}</strong> on {fmtDate(plan.signoff.signed_at)}. Use <em>Start review / edit</em> to create a new draft.</p>
          </div>
        </div>
      )}
      {CP_SECTIONS.map((s) => {
        const level = visLevelFor(plan, s.key);
        const meta = VIS_LEVEL_META[level] || VIS_LEVEL_META.all_staff;
        return (
        <div key={s.key} className="border border-slate-100 rounded-md p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-700 inline-flex items-center gap-2">
                {s.key}
                {s.readOnly && <span className="text-[10px] font-normal text-slate-500">(auto-populated)</span>}
                <span data-testid={`section-vis-level-${s.key.replace(/[^a-z]/gi, "-").toLowerCase()}`} className={`inline-block px-1.5 py-0.5 text-[9px] font-bold rounded-full border ${meta.cls}`}>{meta.label}</span>
              </p>
              <p className="text-[11px] text-slate-500">{s.description}</p>
            </div>
            {isRmLike && (
              <label className="inline-flex items-center gap-1 text-xs text-slate-600 whitespace-nowrap" data-testid={`section-vis-toggle-${s.key.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                <input type="checkbox" checked={!!visibility[s.key]} onChange={(e) => toggleVis(s.key, e.target.checked)} />
                {visibility[s.key] ? <><Eye className="h-3 w-3" /> Family-portal visible</> : <><EyeOff className="h-3 w-3" /> Hidden from family</>}
              </label>
            )}
          </div>
          <p className="mt-2 text-sm text-slate-800 whitespace-pre-wrap">{sections[s.key] || <span className="text-slate-400">—</span>}</p>
        </div>
        );
      })}
      {plan.review_record && (
        <div className="border-t border-slate-100 pt-3 text-xs text-slate-600 space-y-1">
          <p className="font-semibold text-slate-700">Review record</p>
          <p>Reason: {plan.review_record.reason} · Reviewed by {plan.review_record.reviewed_by_name} on {fmtDate(plan.review_record.reviewed_at)}</p>
          {plan.review_record.summary_of_changes && <p>Summary: {plan.review_record.summary_of_changes}</p>}
          <p>Service user involved: {plan.review_record.service_user_involved ? "Yes" : "No"} · Family involved: {plan.review_record.family_involved ? "Yes" : "No"}</p>
        </div>
      )}
    </div>
  );
}

function CarePlanEditor({ draft, onClose, reload, userRole, suType }) {
  const isRm = RM_ONLY.includes(userRole);
  const [sections, setSections] = useState({ ...(draft.sections || {}) });
  const [tasks, setTasks] = useState({ ...(draft.tasks_by_visit_type || {}) });
  const [visLevels, setVisLevels] = useState({ ...(draft.section_visibility_levels || {}) });
  const [medications, setMedications] = useState([...(draft.medications || [])]);
  const [conditions, setConditions] = useState([...(draft.health_conditions || [])]);
  const [allergies, setAllergies] = useState([...(draft.allergies || [])]);
  const [eduChild, setEduChild] = useState({ ...(draft.education_child || {}) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showPublish, setShowPublish] = useState(false);
  const [pubForm, setPubForm] = useState({
    review_reason: "annual_review",
    summary_of_changes: "",
    service_user_involved: false, service_user_involvement_note: "",
    family_involved: false, family_involvement_note: "",
    signoff_declaration: false,
    signoff_attested_to: [],
  });

  const draftPayload = () => ({
    service_user_id: draft.service_user_id,
    sections,
    section_visibility: draft.section_visibility || {},
    section_visibility_levels: visLevels,
    tasks_by_visit_type: tasks,
    medications,
    health_conditions: conditions,
    allergies,
    education_child: suType === "child" ? eduChild : null,
  });

  const saveDraft = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/care-plans", draftPayload());
      reload();
    } catch (e) { setError(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true); setError("");
    try {
      // Save first to ensure draft holds latest content
      await api.post("/care-plans", draftPayload());
      await api.post(`/care-plans/${draft.id}/publish`, pubForm);
      reload();
      onClose();
    } catch (e) { setError(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="care-plan-editor">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">Care plan editor — Draft v{draft.version_number}</h2>
            <p className="text-xs text-slate-500">Risk Summary is auto-populated. Active version remains visible until you publish.</p>
          </div>
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Close</button>
        </header>

        <div className="p-6 space-y-4">
          {CP_SECTIONS.map((s) => {
            const lvl = visLevels[s.key] || DEFAULT_SECTION_VIS_LEVELS[s.key] || "all_staff";
            return (
            <div key={s.key}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{s.key}{s.readOnly && <span className="ml-2 text-[10px] font-normal text-slate-400">(read-only — auto)</span>}</p>
                  <p className="text-[11px] text-slate-500">{s.description}</p>
                </div>
                <label className="text-[11px] text-slate-600 whitespace-nowrap inline-flex items-center gap-1">
                  Visible to:
                  <select
                    value={lvl}
                    onChange={(e) => setVisLevels({ ...visLevels, [s.key]: e.target.value })}
                    data-testid={`vis-level-select-${s.key.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                    className="text-[11px] px-1.5 py-0.5 border border-slate-300 rounded"
                  >
                    {Object.entries(VIS_LEVEL_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                  </select>
                </label>
              </div>
              <textarea
                disabled={!!s.readOnly}
                value={sections[s.key] || ""}
                onChange={(e) => setSections({ ...sections, [s.key]: e.target.value })}
                rows={s.key === "About Me" || s.key === "My Goals" || s.key === "My Daily Routine" ? 4 : 3}
                className={`mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm ${s.readOnly ? "bg-slate-50 text-slate-600" : ""}`}
                data-testid={`care-plan-${s.key.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              />
              {s.key === "Medication" && (
                <div className="mt-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Structured medication list</p>
                  <StructuredListEditor
                    value={medications} onChange={setMedications}
                    fields={MEDICATION_FIELDS}
                    addLabel="Add medication"
                    emptyLabel="No medications recorded. Use the structured list for everything that needs administering."
                    testidPrefix="meds"
                  />
                </div>
              )}
              {s.key === "My Health Needs" && (
                <div className="mt-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Health conditions</p>
                  <StructuredListEditor
                    value={conditions} onChange={setConditions}
                    fields={HEALTH_CONDITION_FIELDS}
                    addLabel="Add condition"
                    emptyLabel="No conditions recorded."
                    testidPrefix="conds"
                  />
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-3 mb-1">Allergies / adverse reactions</p>
                  <StructuredListEditor
                    value={allergies} onChange={setAllergies}
                    fields={ALLERGY_FIELDS}
                    addLabel="Add allergy"
                    emptyLabel="No allergies recorded."
                    testidPrefix="allergies"
                  />
                </div>
              )}
              {s.key === "Education / Activity / Work" && suType === "child" && (
                <div className="mt-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">School / SEN details</p>
                  <EducationChildEditor value={eduChild} onChange={setEduChild} testidPrefix="edu" />
                </div>
              )}
            </div>
            );
          })}

          <fieldset className="border border-slate-200 rounded-md p-3">
            <legend className="text-xs uppercase tracking-wider text-slate-500 px-1">Visit task checklist</legend>
            <p className="text-xs text-slate-500 mb-2">These tasks appear on the care worker's visit notes form. Each task must be ticked or have a reason at check-out.</p>
            <TasksEditor tasks={tasks} onChange={setTasks} />
          </fieldset>

          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>}

          <footer className="sticky bottom-0 bg-white border-t border-slate-200 -mx-6 px-6 py-3 flex justify-between gap-2">
            <button onClick={saveDraft} disabled={busy} data-testid="care-plan-save-draft-button" className="px-4 py-2 border border-slate-300 rounded-md text-sm">{busy ? "Saving…" : "Save draft"}</button>
            {isRm && (
              <button onClick={() => setShowPublish((v) => !v)} data-testid="care-plan-show-publish-button" className="px-4 py-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md font-medium inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Approve & publish</button>
            )}
          </footer>

          {showPublish && isRm && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 space-y-3" data-testid="care-plan-publish-form">
              <p className="text-sm font-semibold text-emerald-900">Publish — review record</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <label>
                  <span className="text-xs uppercase tracking-wider text-slate-500">Reason for review</span>
                  <select value={pubForm.review_reason} onChange={(e) => setPubForm({ ...pubForm, review_reason: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md">
                    <option value="annual_review">Annual review</option>
                    <option value="initial_publication">Initial publication</option>
                    <option value="significant_event">Significant event</option>
                    <option value="condition_change">Condition change</option>
                    <option value="post_hospital_discharge">Post hospital discharge</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label>
                  <span className="text-xs uppercase tracking-wider text-slate-500">Summary of changes</span>
                  <input value={pubForm.summary_of_changes} onChange={(e) => setPubForm({ ...pubForm, summary_of_changes: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" data-testid="publish-summary-input" />
                </label>
                <label className="text-xs uppercase tracking-wider text-slate-500 inline-flex items-center gap-2">
                  <input type="checkbox" checked={pubForm.service_user_involved} onChange={(e) => setPubForm({ ...pubForm, service_user_involved: e.target.checked })} /> Service user involved in review
                </label>
                <label className="text-xs uppercase tracking-wider text-slate-500 inline-flex items-center gap-2">
                  <input type="checkbox" checked={pubForm.family_involved} onChange={(e) => setPubForm({ ...pubForm, family_involved: e.target.checked })} /> Family / advocate involved
                </label>
              </div>
              <div className="rounded border border-emerald-300 bg-white p-3 space-y-2" data-testid="signoff-declaration-block">
                <p className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">Sign-off declaration (required)</p>
                <label className="text-sm flex items-start gap-2 text-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={pubForm.signoff_declaration}
                    onChange={(e) => setPubForm({ ...pubForm, signoff_declaration: e.target.checked })}
                    data-testid="signoff-declaration-checkbox"
                  />
                  <span>
                    I confirm I have reviewed every section of this care plan, it is accurate and person-centred, and discussions have happened with the service user and (where appropriate) their family. <strong>Signing locks this version.</strong>
                  </span>
                </label>
                {["I have reviewed all sections", "I have discussed the plan with the service user", "Family / advocate has been involved (or reason recorded)"].map((stmt) => (
                  <label key={stmt} className="text-xs flex items-start gap-2 text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={(pubForm.signoff_attested_to || []).includes(stmt)}
                      onChange={(e) => {
                        const cur = new Set(pubForm.signoff_attested_to || []);
                        if (e.target.checked) cur.add(stmt); else cur.delete(stmt);
                        setPubForm({ ...pubForm, signoff_attested_to: Array.from(cur) });
                      }}
                      data-testid={`signoff-attest-${stmt.toLowerCase().replace(/[^a-z]/gi, "-")}`}
                    />
                    {stmt}
                  </label>
                ))}
              </div>
              <button onClick={publish} disabled={busy || !pubForm.signoff_declaration} data-testid="care-plan-publish-confirm-button" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold text-sm disabled:opacity-50">{busy ? "Publishing…" : "Confirm — sign and publish v" + draft.version_number}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TasksEditor({ tasks, onChange }) {
  const list = tasks["Regular Visit"] || [];
  const setList = (next) => onChange({ ...tasks, "Regular Visit": next });
  const add = () => setList([...list, { task_id: "t-" + Math.random().toString(36).slice(2, 9), label: "" }]);
  return (
    <div className="space-y-2">
      {list.map((t, i) => (
        <div key={t.task_id || i} className="flex items-center gap-2">
          <input value={t.label} placeholder="Task label" onChange={(e) => setList(list.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} className="flex-1 px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
          <button onClick={() => setList(list.filter((_, idx) => idx !== i))} className="text-xs px-2 py-1 text-red-600 border border-red-200 rounded">Remove</button>
        </div>
      ))}
      <button onClick={add} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md" data-testid="care-plan-add-task-button">+ Add task</button>
    </div>
  );
}

function RisksTab({ risks, suId, suName, userRole }) {
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);

  const canExport = MGMT.includes(userRole) || userRole === "team_leader";
  const visible = (risks || []).filter((r) => showSuperseded || r.status !== "superseded");
  const allSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      visible.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const exportPdf = async (mode /* "selected" | "all" */) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (mode === "selected") {
        params.set("ids", Array.from(selected).join(","));
      } else if (showSuperseded) {
        params.set("include_superseded", "true");
      }
      const qs = params.toString();
      const path = `/service-users/${suId}/risk-assessments/export${qs ? `?${qs}` : ""}`;
      await openAuthedPrintable(path);
    } catch (err) {
      alert(`Export failed: ${err?.response?.data?.detail || err?.message || "unknown error"}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="risks-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Risk assessments</h3>
          <p className="text-xs text-slate-500">
            {suName ? <>For <strong>{suName}</strong> · </> : null}
            CQC Regulation 12 · likelihood × impact scoring
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-1 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={showSuperseded}
              onChange={(e) => setShowSuperseded(e.target.checked)}
              data-testid="risks-show-superseded"
            />
            Show superseded
          </label>
          {canExport && (
            <>
              <button
                type="button"
                onClick={() => exportPdf("selected")}
                disabled={selected.size === 0 || exporting}
                data-testid="risks-export-selected-button"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium bg-white border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px]"
              >
                <FileDown className="h-3.5 w-3.5" />
                Export selected ({selected.size})
              </button>
              <button
                type="button"
                onClick={() => exportPdf("all")}
                disabled={visible.length === 0 || exporting}
                data-testid="risks-export-all-button"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px]"
              >
                <FileDown className="h-3.5 w-3.5" />
                Export all as PDF
              </button>
            </>
          )}
        </div>
      </div>

      {visible.length === 0 && <p className="text-sm text-slate-500">No risk assessments yet.</p>}

      {canExport && visible.length > 0 && (
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 mb-2" data-testid="risks-select-all-label">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            data-testid="risks-select-all"
          />
          Select all ({visible.length})
        </label>
      )}

      <ul className="space-y-2">
        {visible.map((r) => (
          <li
            key={r.id}
            className="flex items-center gap-3 border border-slate-200 rounded-md p-3"
            data-testid={`risk-row-${r.id}`}
          >
            {canExport && (
              <input
                type="checkbox"
                checked={selected.has(r.id)}
                onChange={() => toggle(r.id)}
                data-testid={`risk-select-${r.id}`}
                className="h-4 w-4"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900">{r.risk_type}</p>
              <p className="text-xs text-slate-500">
                Score {r.risk_score} ({r.risk_band}) · Residual {r.residual_score} ({r.residual_band})
                {r.review_date ? ` · Review due ${fmtDate(r.review_date)}` : ""}
              </p>
            </div>
            <StatusBadge status={r.status} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function VisitsTab({ visits }) {
  const [expandedTrail, setExpandedTrail] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState(null);
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="su-visits-tab">
      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Visit history &amp; care notes</h3>
      <ul className="divide-y divide-slate-100">
        {visits.map((v) => {
          const hasNotes = !!v.notes_submitted_at;
          const notesOpen = expandedNotes === v.id;
          const trailOpen = expandedTrail === v.id;
          return (
            <li key={v.id} className="py-3" data-testid={`su-visit-row-${v.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <MapPin className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{fmtDateTimeShort(v.scheduled_start)} {v.worker_name && <span className="font-normal text-slate-600">· {v.worker_name}</span>}</p>
                    <p className="text-xs text-slate-500">
                      {v.actual_duration_mins ? `${v.actual_duration_mins} min` : "Not started"}
                      {v.short_visit_flag && " · short visit"}
                      {v.gps_trail?.length > 0 && ` · ${v.gps_trail.length} GPS points`}
                      {v.visit_type && ` · ${v.visit_type}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={v.status} />
                  {hasNotes && (
                    <button
                      onClick={() => setExpandedNotes(notesOpen ? null : v.id)}
                      data-testid={`su-visit-notes-toggle-${v.id}`}
                      className="text-xs text-[#0D7377] font-medium px-2 py-1 border border-slate-300 rounded inline-flex items-center gap-1"
                    >
                      <NotebookPen className="h-3 w-3" />
                      {notesOpen ? "Hide notes" : "View notes"}
                    </button>
                  )}
                  {!hasNotes && v.status === "completed" && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">Notes pending</span>
                  )}
                  {(v.gps_trail?.length || 0) > 0 && (
                    <button onClick={() => setExpandedTrail(trailOpen ? null : v.id)} data-testid={`gps-trail-toggle-${v.id}`} className="text-xs text-[#0D7377] font-medium px-2 py-1 border border-slate-300 rounded">
                      {trailOpen ? "Hide trail" : "Show trail"}
                    </button>
                  )}
                </div>
              </div>
              {notesOpen && hasNotes && <VisitNotesView visit={v} />}
              {trailOpen && v.gps_trail && (
                <div className="mt-3">
                  <VisitTrail trail={v.gps_trail} expectedAddress={null} />
                </div>
              )}
            </li>
          );
        })}
        {visits.length === 0 && <li className="py-3 text-sm text-slate-500">No visits.</li>}
      </ul>
    </div>
  );
}

function VisitNotesView({ visit }) {
  const n = visit.visit_notes || {};
  const tasks = Array.isArray(n.tasks) ? n.tasks : [];
  const completed = tasks.filter((t) => t.completed);
  const incomplete = tasks.filter((t) => !t.completed);
  const photos = Array.isArray(visit.photos) ? visit.photos : [];
  const moodColor = (() => {
    const m = (n.mood || "").toLowerCase();
    if (/distressed|agitated/.test(m)) return "bg-red-50 border-red-300 text-red-800";
    if (/tearful|anxious|withdrawn|confused/.test(m)) return "bg-amber-50 border-amber-300 text-amber-800";
    if (/bright|calm|settled/.test(m)) return "bg-emerald-50 border-emerald-300 text-emerald-800";
    return "bg-slate-50 border-slate-300 text-slate-700";
  })();
  return (
    <div className="mt-3 ml-7 bg-slate-50 border border-slate-200 rounded-md p-4 space-y-3" data-testid={`su-visit-notes-view-${visit.id}`}>
      <p className="text-xs uppercase tracking-wider text-slate-500">
        Submitted {visit.notes_submitted_at && fmtDateTimeShort(visit.notes_submitted_at)}
        {visit.worker_name && ` by ${visit.worker_name}`}
      </p>

      {n.mood && (
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Mood &amp; wellbeing</p>
          <span className={`inline-block text-sm border rounded-md px-3 py-1.5 ${moodColor}`} data-testid={`su-visit-mood-${visit.id}`}>
            {n.mood}
          </span>
        </div>
      )}

      {tasks.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Tasks ({completed.length}/{tasks.length} completed)</p>
          <ul className="space-y-1">
            {completed.map((t) => (
              <li key={t.task_id || t.label} className="text-sm text-slate-700 flex items-start gap-2">
                <span className="text-emerald-600 mt-0.5">✓</span>
                <span>{t.label}</span>
              </li>
            ))}
            {incomplete.map((t) => (
              <li key={t.task_id || t.label} className="text-sm text-amber-900 flex items-start gap-2">
                <AlertTriangle className="h-3 w-3 text-amber-600 mt-1 shrink-0" />
                <span><span className="font-medium">{t.label}</span> — <span className="italic">{t.reason_if_not_completed || "no reason given"}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(n.meals || n.fluids) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {n.meals && <NotesField label="Meals" value={n.meals} testid={`su-visit-meals-${visit.id}`} />}
          {n.fluids && <NotesField label="Fluids" value={n.fluids} testid={`su-visit-fluids-${visit.id}`} />}
        </div>
      )}

      {n.physical_observations && <NotesField label="Physical observations" value={n.physical_observations} testid={`su-visit-obs-${visit.id}`} />}
      {n.medication_notes && <NotesField label="Medication notes" value={n.medication_notes} testid={`su-visit-meds-${visit.id}`} />}
      {n.concerns && <NotesField label="Concerns / observations" value={n.concerns} highlight testid={`su-visit-concerns-${visit.id}`} />}

      {photos.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Photos ({photos.length})</p>
          <div className="flex flex-wrap gap-2">
            {photos.map((p) => (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="block">
                <img src={p.url} alt="Visit photo" className="h-20 w-20 object-cover rounded border border-slate-300" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotesField({ label, value, highlight, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
      <p className={`text-sm whitespace-pre-wrap ${highlight ? "text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1" : "text-slate-800"}`}>{value}</p>
    </div>
  );
}

function IncidentsTab({ incidents }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Incidents</h3>
      <ul className="space-y-2">
        {incidents.map((i) => (
          <li key={i.id} className="border border-slate-200 rounded-md p-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> {i.category}</p>
              <p className="text-xs text-slate-500">{fmtDate(i.date_of_incident)}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={i.severity} />
              <StatusBadge status={i.status} />
            </div>
          </li>
        ))}
        {incidents.length === 0 && <p className="text-sm text-slate-500">No incidents recorded.</p>}
      </ul>
    </div>
  );
}

function NotesTab({ notes, suId, userRole, reload }) {
  const [noteCat, setNoteCat] = useState("General");
  const [noteText, setNoteText] = useState("");
  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    await api.post("/daily-notes", { service_user_id: suId, category: noteCat, note_text: noteText });
    setNoteText(""); reload();
  };
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-4">
      <h3 className="font-heading text-lg font-semibold text-slate-900">Daily notes</h3>
      {userRole !== "family_member" && (
        <form onSubmit={addNote} className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select value={noteCat} onChange={(e) => setNoteCat(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md">
            {["General", "Health & Wellbeing", "Behaviour", "Social & Community", "Communication", "Personal Development"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea required placeholder="Note…" value={noteText} onChange={(e) => setNoteText(e.target.value)} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md" data-testid="daily-note-textarea" />
          <button type="submit" data-testid="add-daily-note-button" className="bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium inline-flex items-center justify-center gap-2"><NotebookPen className="h-4 w-4" /> Add</button>
        </form>
      )}
      <ul className="divide-y divide-slate-100">
        {notes.map((n) => (
          <li key={n.id} className="py-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-900">{n.category}</p>
              <p className="text-xs text-slate-500">{fmtDateTimeShort(n.created_at)} · {n.worker_name}</p>
            </div>
            <p className="mt-1 text-sm text-slate-700">{n.note_text}</p>
          </li>
        ))}
        {notes.length === 0 && <p className="text-sm text-slate-500">No notes yet.</p>}
      </ul>
    </div>
  );
}

function GpVerificationStatus({ gp }) {
  const last = gp?.last_verified_at;
  if (!last) return <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 inline-block">Never verified</p>;
  const days = (new Date() - new Date(last)) / 86400000;
  const overdue = days > 183;
  const dueSoon = !overdue && days > 150;
  return (
    <p className={`mt-2 text-xs px-2 py-1 inline-block rounded ${overdue ? "bg-red-50 border border-red-200 text-red-700" : dueSoon ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-emerald-50 border border-emerald-200 text-emerald-800"}`}>
      Last verified {fmtDate(last)} {gp?.verified_by_name ? `by ${gp.verified_by_name}` : ""}{overdue ? " · OVERDUE (>6 months)" : dueSoon ? " · Due soon" : ""}
    </p>
  );
}

function GpVerifyButton({ suId, reload }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const submit = async () => {
    setBusy(true);
    try { await api.put(`/service-users/${suId}/gp-verification`, { notes }); setOpen(false); setNotes(""); reload(); } finally { setBusy(false); }
  };
  if (!open) return <button onClick={() => setOpen(true)} data-testid="gp-verify-button" className="px-3 py-1 text-xs bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md font-medium whitespace-nowrap">Verify GP</button>;
  return (
    <div className="space-y-2 min-w-0">
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="w-full px-2 py-1 border border-slate-300 rounded text-xs" data-testid="gp-verify-notes" />
      <div className="flex gap-1">
        <button onClick={() => setOpen(false)} className="text-xs px-2 py-1 border border-slate-300 rounded">Cancel</button>
        <button disabled={busy} onClick={submit} data-testid="gp-verify-confirm" className="text-xs px-2 py-1 bg-[#0D7377] text-white rounded">Confirm</button>
      </div>
    </div>
  );
}

function AssignedStaffPanel({ su, reload, userRole }) {
  const [staff, setStaff] = useState([]);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ worker_id: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isMgmt = MGMT.includes(userRole);
  const isRm = RM_LIKE.includes(userRole);

  useEffect(() => {
    if (isMgmt) {
      api.get("/staff").then(({ data }) => setStaff(data.items || [])).catch(() => {});
      api.get(`/service-users/${su.id}/key-worker-history`).then(({ data }) => setHistory(data.items || [])).catch(() => {});
    }
  }, [su.id, isMgmt]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      await api.put(`/service-users/${su.id}/key-worker`, form);
      setOpen(false); setForm({ worker_id: "", reason: "" });
      reload();
      const { data } = await api.get(`/service-users/${su.id}/key-worker-history`);
      setHistory(data.items || []);
    } catch (err) { setError(err.response?.data?.detail || err.message); } finally { setBusy(false); }
  };

  const currentKw = staff.find((s) => s.id === su.key_worker_id);
  const eligible = staff.filter((s) => ["care_worker", "bank_worker", "team_leader", "registered_manager", "deputy_manager"].includes(s.role));

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-5">
      <section>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500">Key Worker</p>
            <p className="font-heading text-lg font-semibold text-slate-900" data-testid="key-worker-name">{currentKw ? currentKw.full_name : "Not assigned"}</p>
            {currentKw && <p className="text-xs text-slate-500">{currentKw.role.replace(/_/g, " ")} · {currentKw.email}</p>}
            {su.key_worker_changed_at && <p className="text-xs text-slate-500 mt-0.5">Last changed {fmtDate(su.key_worker_changed_at)}</p>}
          </div>
          {isRm && (
            <button onClick={() => setOpen((v) => !v)} data-testid="change-key-worker-button" className="bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md text-sm font-medium">{currentKw ? "Change Key Worker" : "Assign Key Worker"}</button>
          )}
        </div>
        {open && (
          <form onSubmit={submit} className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-slate-100 pt-3" data-testid="key-worker-form">
            <select required value={form.worker_id} onChange={(e) => setForm({ ...form, worker_id: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
              <option value="">Choose worker…</option>
              {eligible.map((s) => <option key={s.id} value={s.id}>{s.full_name} — {s.role.replace(/_/g, " ")}</option>)}
            </select>
            <input required minLength={5} placeholder="Reason for change (audit log)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" data-testid="key-worker-reason" />
            {error && <p className="md:col-span-2 text-sm text-red-700">{error}</p>}
            <div className="md:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-slate-300 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="key-worker-submit" className="px-4 py-2 bg-[#0D7377] text-white rounded-md font-medium">Confirm assignment</button>
            </div>
          </form>
        )}
      </section>

      <section>
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Assigned care team</p>
        <ul className="divide-y divide-slate-100">
          {(su.assigned_worker_ids || []).map((wid) => {
            const w = staff.find((s) => s.id === wid);
            if (!w) return null;
            return (
              <li key={wid} className="py-2 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">{w.full_name}</p>
                  <p className="text-xs text-slate-500">{w.role.replace(/_/g, " ")}</p>
                </div>
                {wid === su.key_worker_id && <span className="text-xs px-2 py-0.5 rounded-full bg-[#E0F2F2] text-[#0D7377] font-semibold">Key Worker</span>}
              </li>
            );
          })}
          {(su.assigned_worker_ids || []).length === 0 && <li className="py-2 text-sm text-slate-500">No staff assigned.</li>}
        </ul>
      </section>

      {isMgmt && history.length > 0 && (
        <section>
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Key Worker history</p>
          <ul className="divide-y divide-slate-100" data-testid="key-worker-history">
            {history.map((h) => (
              <li key={h.id} className="py-2">
                <p className="text-sm text-slate-800">{h.from_worker_id ? "Reassigned" : "Assigned"} on {fmtDate(h.changed_at)} by {h.changed_by_name}</p>
                <p className="text-xs italic text-slate-500">"{h.reason}"</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}


function FamilyMembersPanel({ suId, userRole, suName }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    relationship: "Daughter",
    is_next_of_kin: false,
    can_view_care_plan: true,
    can_message_team: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);
  const canManage = ["registered_manager", "deputy_manager", "super_admin"].includes(userRole);

  const load = async () => {
    try {
      const { data } = await api.get(`/service-users/${suId}/family-members`);
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load family members");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suId]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess(null);
    setBusy(true);
    try {
      const { data } = await api.post(`/service-users/${suId}/family-members`, form);
      setSuccess({
        email: data.email,
        temp_password: data.temp_password,
        created: data.created,
      });
      setForm({
        full_name: "",
        email: "",
        relationship: "Daughter",
        is_next_of_kin: false,
        can_view_care_plan: true,
        can_message_team: true,
      });
      setAdding(false);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to invite family member");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (userId, name) => {
    if (!window.confirm(`Remove ${name} as a family contact for ${suName}?\n\nThis will revoke their portal access to this service user.`)) return;
    try {
      await api.delete(`/service-users/${suId}/family-members/${userId}?deactivate=true`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to remove family member");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-4" data-testid="family-members-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Family portal access</h3>
          <p className="text-sm text-slate-600">People who can log in to Rabah Concept and see {suName}'s care.</p>
        </div>
        {canManage && !adding && (
          <button
            onClick={() => { setAdding(true); setSuccess(null); setErr(""); }}
            data-testid="add-family-member-button"
            className="inline-flex items-center gap-1.5 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-3 py-1.5 rounded-md text-sm font-medium"
          >
            <UserPlus className="h-4 w-4" /> Invite family member
          </button>
        )}
      </div>

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-900" data-testid="family-invite-success">
          <p className="font-semibold">Invitation {success.created ? "sent" : "linked"} to {success.email}.</p>
          {success.created && success.temp_password && (
            <p className="mt-1 text-xs">
              Temporary password: <code className="bg-white px-2 py-0.5 rounded border border-emerald-300">{success.temp_password}</code> — they will be asked to change it on first login. (Also sent by email if SMTP is configured.)
            </p>
          )}
        </div>
      )}

      {err && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="family-error">{err}</p>}

      {adding && canManage && (
        <form onSubmit={submit} className="border border-slate-200 rounded-md p-4 space-y-3 bg-slate-50" data-testid="family-invite-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Full name <span className="text-rose-600">*</span></label>
              <input
                required minLength={2}
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="e.g. Sarah Whitfield"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="family-full-name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email <span className="text-rose-600">*</span></label>
              <input
                required type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="sarah@example.com"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="family-email"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Relationship</label>
              <select
                value={form.relationship}
                onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="family-relationship"
              >
                <option>Daughter</option>
                <option>Son</option>
                <option>Spouse / Partner</option>
                <option>Parent</option>
                <option>Sibling</option>
                <option>Grandchild</option>
                <option>Friend</option>
                <option>Advocate</option>
                <option>Power of Attorney</option>
                <option>Other</option>
              </select>
            </div>
            <div className="flex items-center pt-6">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_next_of_kin}
                  onChange={(e) => setForm({ ...form, is_next_of_kin: e.target.checked })}
                  data-testid="family-nok"
                />
                Next of kin (primary contact for emergencies)
              </label>
            </div>
          </div>
          <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-2 border-t border-slate-200 pt-3">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.can_view_care_plan}
                onChange={(e) => setForm({ ...form, can_view_care_plan: e.target.checked })}
              />
              Can view care plan
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.can_message_team}
                onChange={(e) => setForm({ ...form, can_message_team: e.target.checked })}
              />
              Can message care team
            </label>
          </fieldset>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAdding(false)} className="text-sm px-3 py-1.5 border border-slate-300 bg-white text-slate-700 rounded-md">Cancel</button>
            <button
              type="submit"
              disabled={busy}
              data-testid="family-invite-submit"
              className="text-sm px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-md font-semibold"
            >
              {busy ? "Sending…" : "Send invitation"}
            </button>
          </div>
        </form>
      )}

      <ul className="divide-y divide-slate-100" data-testid="family-members-list">
        {items.map((u) => (
          <li key={u.id} className="py-3 flex items-start justify-between gap-3" data-testid={`family-row-${u.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                {u.full_name}
                {u.is_next_of_kin && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded">Next of kin</span>
                )}
                {u.status === "inactive" && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-slate-100 text-slate-600 border border-slate-300 rounded">Inactive</span>
                )}
              </p>
              <p className="text-xs text-slate-500">
                {u.email}
                {u.relationship && <span className="ml-1">· {u.relationship}</span>}
                {u.linked_at && <span className="ml-1">· linked {fmtDate(u.linked_at)}</span>}
              </p>
              <p className="mt-0.5 text-[11px] text-slate-500">
                {u.can_view_care_plan ? "✓ care plan" : "— care plan hidden"}
                {" · "}
                {u.can_message_team ? "✓ messaging" : "— no messaging"}
              </p>
            </div>
            {canManage && (
              <button
                onClick={() => unlink(u.id, u.full_name)}
                data-testid={`family-unlink-${u.id}`}
                className="p-1.5 text-rose-700 hover:bg-rose-50 rounded-md"
                title="Remove from this service user"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </li>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-slate-500 py-4" data-testid="family-empty">
            No family members linked yet. {canManage && "Click \u201CInvite family member\u201D to give a relative read-only access to the care record."}
          </p>
        )}
      </ul>
    </div>
  );
}
